import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOrganization } from '../../contexts/OrganizationContext';
import { fetchMissions } from '../../services/missionService';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import { Box, Typography, Table, TableHead, TableRow, TableCell, TableBody, Button, CircularProgress } from '@mui/material';

const MissionCompletion = () => {
    const navigate = useNavigate();
    const { selectedOrg } = useOrganization();
    const [missions, setMissions] = useState([]);
    const [loading, setLoading] = useState(true);

    // Fetch missions on load
    useEffect(() => {
        const getMissions = async () => {
            try {
                const data = await fetchMissions(selectedOrg); // Ensure this fetches all missions
                setMissions(data);
            } catch (err) {
                console.error('Failed to fetch missions:', err);
            } finally {
                setLoading(false);
            }
        };
        getMissions();
    }, [selectedOrg]);

    // Calculate Completion Rates
    const totalMissions = missions.length;
    const completedMissions = missions.filter((mission) => mission.completedAt).length;
    const inProgressMissions = missions.filter((mission) => mission.startedAt && !mission.completedAt).length;
    const notStartedMissions = missions.filter((mission) => !mission.startedAt).length;

    const completedPercentage = totalMissions > 0 ? ((completedMissions / totalMissions) * 100).toFixed(2) : 0;
    const inProgressPercentage = totalMissions > 0 ? ((inProgressMissions / totalMissions) * 100).toFixed(2) : 0;
    const notStartedPercentage = totalMissions > 0 ? ((notStartedMissions / totalMissions) * 100).toFixed(2) : 0;

    const handleBackToReports = () => {
        navigate('/reports');
    };

    return (
        <Box sx={{ padding: '16px' }}>
            {/* Back to Reports Button */}
            <Button
                variant="text"
                onClick={handleBackToReports}
                startIcon={<ArrowBackIcon />}
                sx={{
                    marginBottom: '16px',
                    color: '#2c3e50',
                    textTransform: 'none',
                    fontWeight: 400,
                    '&:hover': {
                        backgroundColor: '#f0f0f0',
                    },
                }}
            >
                Reports
            </Button>

            <Typography variant="h4" gutterBottom>
                Mission Completion Report
            </Typography>

            {/* Show loading spinner while fetching data */}
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    {/* Completion Stats */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: '24px' }}>
                        <Box>
                            <Typography variant="h6">Total Missions</Typography>
                            <Typography variant="h5">{totalMissions}</Typography>
                        </Box>
                        <Box>
                            <Typography variant="h6">Completed</Typography>
                            <Typography variant="h5">{completedMissions} ({completedPercentage}%)</Typography>
                        </Box>
                        <Box>
                            <Typography variant="h6">In Progress</Typography>
                            <Typography variant="h5">{inProgressMissions} ({inProgressPercentage}%)</Typography>
                        </Box>
                        <Box>
                            <Typography variant="h6">Not Started</Typography>
                            <Typography variant="h5">{notStartedMissions} ({notStartedPercentage}%)</Typography>
                        </Box>
                    </Box>

                    {/* Mission Table */}
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Mission Name</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Created At</TableCell>
                                <TableCell>Completed At</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {missions.map((mission) => (
                                <TableRow key={mission.missionGuid}>
                                    <TableCell>{mission.missionName || 'Unnamed Mission'}</TableCell>
                                    <TableCell>
                                        {mission.completedAt
                                            ? 'Completed'
                                            : mission.startedAt
                                                ? 'In Progress'
                                                : 'Not Started'}
                                    </TableCell>
                                    <TableCell>{new Date(mission.createdAt).toLocaleDateString()}</TableCell>
                                    <TableCell>
                                        {mission.completedAt
                                            ? new Date(mission.completedAt).toLocaleDateString()
                                            : 'N/A'}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </>
            )}
        </Box>
    );
};

export default MissionCompletion;